<template>
  <div class="then-row" :class="{ 'sequence-template': isSequenceTemplate, 'then-row--without-delete': !showDeleteAction, 'then-row--invalid': hasValidationError }">
    <span class="then-label">THEN</span>
    <custom-dropdown
      v-if="showActionFieldDropdown"
      :list="actionFieldItems"
      :selected="selectedActionField"
      :default-style="actionTypeStyle"
      prevent-deselection
      @select="updateAction('field', $event)"
    >
      <template #label="{ item, defaultLabel }">
        <div class="elipsis-text mr-4" v-overflow-tooltip>
          {{ displayOption(item) || defaultLabel }}
        </div>
      </template>
      <template #item="{ item }">
        <div class="elipsis-text list-name" v-overflow-tooltip>{{ displayOption(item) }}</div>
      </template>
    </custom-dropdown>
    <span v-if="!isSequenceTemplate" class="then-equals">=</span>
    <component
      :is="actionFieldComponent"
      :key="actionInputKey"
      :value="actionFieldValue"
      :list="actionFieldOptions"
      :default-style="actionFieldStyle"
      :name="`action-field-${action.id}`"
      @input="updateAction('value', $event)"
    />
    <button v-if="showDeleteAction" class="icon-btn delete-action faint" type="button" @click="$emit('remove', action.id)">
      <delete-icon width="14px" height="14px" />
    </button>
    <p v-if="hasNegativeValueError" class="then-row__error">
      Negative values are not allowed for Number fields.
    </p>
    <p v-else-if="addAttemptError" class="then-row__error">
      Complete the target field and value before adding another set.
    </p>
  </div>
</template>

<script>
import CustomDropdown from '@/components/common/customDropdown.vue'
import DeleteIcon from '@/components/common/icons/deleteIcon.vue'
import { clone } from './configModel'
import { CONFIG_DYNAMIC_ELEMENTS } from './configDynamicElements'
import { getActionTargetItems, getActionValueItems, getCustomListLookupKey, isNegativeValue, isSequenceTemplateConfig, normalizeInputType, optionDisplayLabel, optionValue, resolveActionOption, shouldRestrictNegativeActionValue, withValidationStyle } from './conditionHelper'
import { INPUT_TYPES } from './configConstants'
import { mapGetters } from 'vuex/dist/vuex.common.js'
import config from '@/config'

const SET_ACTION_TYPE = { id: 'SET', name: 'SET', value: 'SET' }

export default {
  name: 'ActionRow',
  components: {
    CustomDropdown,
    DeleteIcon
  },
  props: {
    action: {
      type: Object,
      required: true
    },
    actions: {
      type: Array,
      default: () => []
    },
    lists: {
      type: Object,
      required: true
    },
    selectedTab: {
      type: Object,
      required: true
    },
    selectedConfig: {
      type: Object,
      required: true
    },
    validation: {
      type: Object,
      default: () => ({})
    },
    addAttemptError: {
      type: Boolean,
      default: false
    },
    showDeleteAction: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      dropdownStyles: {
        actionType: {
          dropDownWidth: '176px',
          borderColor: '#9ee6b8',
          backGroundColor: '#effdf4',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        actionField: {
          dropDownWidth: '180px',
          borderColor: '#e1e7ef',
          backGroundColor: '#ffffff',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        }
      }
    }
  },
  computed: {
    ...mapGetters('tenantConfig', ['getCustomListMap']),
    isSequenceTemplate () {
      return isSequenceTemplateConfig(this.selectedConfig)
    },
    showActionFieldDropdown () {
      return !this.isSequenceTemplate
    },
    actionTypeStyle () {
      return withValidationStyle(this.dropdownStyles.actionType, this.validation.field)
    },
    selectedActionField () {
      return resolveActionOption(this.actionFieldItems, this.action.field) || {}
    },
    selectedFieldDefinition () {
      const selectedValue = optionValue(this.action.field)
      return this.actionFieldItems.find(item => optionValue(item) === selectedValue) ||
        (this.action.field && typeof this.action.field === 'object' ? this.action.field : {})
    },
    selectedInputType () {
      if (this.isSequenceTemplate) {
        return 'list'
      }
      return normalizeInputType(this.selectedFieldDefinition.type) || INPUT_TYPES.TEXT
    },
    actionCustomListKey () {
      if (this.isSequenceTemplate || INPUT_TYPES.CUSTOM_LIST !== this.selectedInputType) {
        return null
      }

      return getCustomListLookupKey(this.selectedFieldDefinition)
    },
    actionFieldComponent () {
      if (this.isSequenceTemplate) {
        return CONFIG_DYNAMIC_ELEMENTS.list
      }
      return CONFIG_DYNAMIC_ELEMENTS[this.selectedInputType] || CONFIG_DYNAMIC_ELEMENTS.text
    },
    actionFieldOptions () {
      if (this.isSequenceTemplate) {
        return getActionValueItems({ selectedConfig: this.selectedConfig, lists: this.lists })
      }
      if (INPUT_TYPES.CUSTOM_LIST === this.selectedInputType) {
        return this.getCustomListMap[this.actionCustomListKey] || []
      }
      return []
    },
    actionFieldValue () {
      if (this.isSequenceTemplate) {
        return this.action.value
      }
      if (INPUT_TYPES.CUSTOM_LIST === this.selectedInputType) {
        return this.action.value
      }
      const value = optionValue(this.action.value)
      return value === undefined || value === null ? '' : value
    },
    actionFieldItems () {
      if (this.isSequenceTemplate) {
        return []
      }
      const items = getActionTargetItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.selectedConfig,
        actions: this.actions,
        actionId: this.action.id,
        actionType: 'SET'
      })
      const filteredItems = (optionValue(this.selectedConfig) === 'STANDARD_MATERIAL_FORM'
        ? items.filter(item => item?.type !== config.FORM_TYPE.TABLE && item?.type !== config.FORM_TYPE.DATE && item?.type !== config.FORM_TYPE.TIME)
        : items).filter(item => !this.isErpIdField(item))
      const selectedField = resolveActionOption(filteredItems, this.action.field)

      return selectedField || !this.action.field
        ? filteredItems
        : [...filteredItems, this.action.field]
    },
    hasNegativeValueError () {
      return shouldRestrictNegativeActionValue(this.action, this.selectedConfig) && (Boolean(this.validation?.negativeValue) || isNegativeValue(this.action.value))
    },
    actionFieldStyle () {
      return withValidationStyle(this.dropdownStyles.actionField, this.validation.value || this.hasNegativeValueError)
    },
    actionInputKey () {
      return [
        this.action.id,
        optionValue(this.selectedConfig) || 'no-config',
        optionValue(this.action.field) || 'no-field',
        this.selectedInputType
      ].join('-')
    },
    hasValidationError () {
      return Object.values(this.validation || {}).some(Boolean) || this.hasNegativeValueError
    }
  },
  methods: {
    isErpIdField (item) {
      return this.displayOption(item)?.trim()?.toLowerCase() === 'erp material id'
    },
    getDefaultValueForField (field) {
      return normalizeInputType(field?.type) === INPUT_TYPES.BOOLEAN ? true : ''
    },
    displayOption (value) {
      return optionDisplayLabel(value)
    },
    updateAction (key, value) {
      const next = clone(this.action)
      next.type = SET_ACTION_TYPE
      next[key] = value
      if (key === 'field') {
        next.value = this.getDefaultValueForField(value)
      }
      this.$emit('update', next)
    }
  }
}
</script>

<style lang="scss" scoped>
.then-row {
  display: grid;
  align-items: center;
  grid-template-columns: 40px 176px 24px minmax(180px, 1fr) 24px;
  gap: 8px;
  margin-bottom: 12px;
  position: relative;
}

.then-row.sequence-template {
  grid-template-columns: 40px minmax(180px, 1fr) 24px;
}

.then-row.then-row--without-delete {
  grid-template-columns: 40px 176px 24px minmax(180px, 1fr);
}

.then-row.then-row--invalid {
  padding-bottom: 28px;
}

.then-row.sequence-template.then-row--without-delete {
  grid-template-columns: 40px minmax(180px, 1fr);
}

.then-label {
  color: #00a651;
  font-size: var(--text-xs2);
  font-weight: 600;
}

.then-equals {
  display: grid;
  place-items: center;
  color: #98a2b3;
  font-size: var(--text-s);
  font-weight: 600;
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.delete-action:hover {
  background: #fff1f0;
}

.delete-action:hover ::v-deep .delete-icon {
  color: #f04438;
}

.faint {
  opacity: 0.65;
}

.then-row__error {
  position: absolute;
  left: 40px;
  bottom: 4px;
  margin: 0;
  color: #dc2626;
  font-size: var(--text-xs2);
  font-weight: 500;
}

::v-deep .custom-dropdown-container,
::v-deep .custom-select {
  width: 100%;
  max-width: 100%;
}

@media (max-width: 900px) {
  .then-row {
    grid-template-columns: 1fr;
  }

  .then-row.sequence-template {
    grid-template-columns: 1fr;
  }

  .then-label {
    display: none;
  }

  .then-row__error {
    position: static;
  }
}
</style>
